<script setup lang="ts">
import Icon from './Icon.vue';

withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
    size?: 'sm' | 'md' | 'lg';
    icon?: string;
    iconRight?: string;
    loading?: boolean;
    disabled?: boolean;
    block?: boolean;
    type?: 'button' | 'submit';
    square?: boolean;
  }>(),
  { variant: 'secondary', size: 'md', type: 'button' },
);
</script>

<template>
  <button
    :type="type"
    class="btn"
    :class="[`btn--${variant}`, `btn--${size}`, { 'btn--block': block, 'btn--square': square, 'is-loading': loading }]"
    :disabled="disabled || loading"
  >
    <span v-if="loading" class="spinner" />
    <Icon v-else-if="icon" :name="icon" :size="size === 'sm' ? 14 : 16" />
    <span v-if="$slots.default" class="label"><slot /></span>
    <Icon v-if="iconRight && !loading" :name="iconRight" :size="size === 'sm' ? 14 : 16" />
  </button>
</template>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition:
    background var(--dur) var(--ease),
    border-color var(--dur) var(--ease),
    color var(--dur) var(--ease),
    transform 80ms var(--ease),
    box-shadow var(--dur) var(--ease);
}
.btn:active:not(:disabled) {
  transform: translateY(1px);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn--sm {
  height: 28px;
  padding: 0 10px;
  font-size: 12.5px;
}
.btn--md {
  height: 34px;
  padding: 0 14px;
}
.btn--lg {
  height: 42px;
  padding: 0 18px;
  font-size: 14.5px;
}
.btn--square.btn--sm {
  width: 28px;
  padding: 0;
}
.btn--square.btn--md {
  width: 34px;
  padding: 0;
}
.btn--block {
  width: 100%;
}

.btn--primary {
  background: linear-gradient(180deg, var(--accent), var(--accent-strong));
  color: var(--on-accent);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.15) inset,
    0 4px 14px -4px rgba(91, 108, 255, 0.55);
}
.btn--primary:hover:not(:disabled) {
  filter: brightness(1.08);
}
.btn--secondary {
  background: var(--surface-2);
  border-color: var(--border);
  color: var(--text);
}
.btn--secondary:hover:not(:disabled) {
  background: var(--surface-hover);
  border-color: var(--border-strong);
}
.btn--ghost {
  background: transparent;
  color: var(--text-2);
}
.btn--ghost:hover:not(:disabled) {
  background: var(--surface-hover);
  color: var(--text);
}
.btn--subtle {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.btn--subtle:hover:not(:disabled) {
  filter: brightness(1.15);
}
.btn--danger {
  background: var(--danger-soft);
  color: var(--danger);
  border-color: transparent;
}
.btn--danger:hover:not(:disabled) {
  background: var(--danger);
  color: #fff;
}

.spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: st-spin 0.7s linear infinite;
}
.label {
  display: inline-flex;
  align-items: center;
}
</style>
